import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Descriptions, Modal, Segmented, message } from 'antd';
import dayjs from 'dayjs';
import { formatAmountText } from '../../../utils/numberFormat';
import { labelOfDebtType } from '../../../constants/debtEnums';

const DebtInfoAndDebtorSelectorCard = ({
  selectedDebt,
  debtorTypeOptions,
  debtorViewType,
  setDebtorViewType,
  selectedDebtorKeys,
  rowByKey,
  currentDebtorRows,
  applySelectedDebtors,
}) => {
  const [debtorDetailOpen, setDebtorDetailOpen] = useState(false);
  const [debtorDetailRow, setDebtorDetailRow] = useState(null);

  const currentSelectedKeys = useMemo(
    () => selectedDebtorKeys.filter((k) => rowByKey?.[k]?.type === debtorViewType),
    [debtorViewType, rowByKey, selectedDebtorKeys],
  );

  const currentRowKeys = useMemo(
    () => (currentDebtorRows || []).map((r) => r.key).filter(Boolean),
    [currentDebtorRows],
  );

  const currentAllChecked = currentRowKeys.length > 0 && currentSelectedKeys.length === currentRowKeys.length;
  const currentIndeterminate = currentSelectedKeys.length > 0 && currentSelectedKeys.length < currentRowKeys.length;

  const otherType = debtorViewType === 'P' ? 'E' : 'P';
  const otherSelectedKeys = useMemo(
    () => selectedDebtorKeys.filter((k) => rowByKey?.[k]?.type === otherType),
    [otherType, rowByKey, selectedDebtorKeys],
  );

  if (!selectedDebt) return null;

  return (
    <>
      <Descriptions title="已选债权信息" bordered size="small" column={2}>
        <Descriptions.Item label="编号">{selectedDebt?.debt?.debtNo}</Descriptions.Item>
        <Descriptions.Item label="债权名称">{selectedDebt?.debt?.title}</Descriptions.Item>
        <Descriptions.Item label="债权类型">{labelOfDebtType(selectedDebt?.debt?.debtType)}</Descriptions.Item>
        <Descriptions.Item label="发生日">{selectedDebt?.debt?.debtOccurrenceDate ? dayjs(selectedDebt.debt.debtOccurrenceDate).format('YYYY-MM-DD') : ''}</Descriptions.Item>
        <Descriptions.Item label="本金金额">{formatAmountText(selectedDebt?.debt?.principalAmount)}</Descriptions.Item>
        <Descriptions.Item label="合计金额">{formatAmountText(selectedDebt?.debt?.totalAmount)}</Descriptions.Item>
      </Descriptions>

      {selectedDebt?.creditor && (
        <Descriptions title="债权人信息" bordered size="small" column={2} style={{ marginTop: 12 }}>
          {String(selectedDebt?.creditor?.creditorType) === 'P' ? (
            <>
              <Descriptions.Item label="姓名">{selectedDebt?.creditor?.name}</Descriptions.Item>
              <Descriptions.Item label="身份证号">{selectedDebt?.creditor?.idCardNo}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{selectedDebt?.creditor?.phone}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{selectedDebt?.creditor?.email}</Descriptions.Item>
            </>
          ) : (
            <>
              <Descriptions.Item label="企业名称">{selectedDebt?.creditor?.companyName}</Descriptions.Item>
              <Descriptions.Item label="统一社会信用代码">{selectedDebt?.creditor?.socialCreditCode}</Descriptions.Item>
              <Descriptions.Item label="法定代表人姓名">{selectedDebt?.creditor?.legalPersonName}</Descriptions.Item>
              <Descriptions.Item label="法定代表人身份证">{selectedDebt?.creditor?.legalPersonIdNo}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{selectedDebt?.creditor?.phone}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{selectedDebt?.creditor?.email}</Descriptions.Item>
            </>
          )}
        </Descriptions>
      )}

      {debtorTypeOptions.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
            <Segmented
              value={debtorViewType}
              options={debtorTypeOptions}
              onChange={(v) => setDebtorViewType(String(v || 'P'))}
            />
            <Checkbox
              indeterminate={currentIndeterminate}
              checked={currentAllChecked}
              disabled={currentRowKeys.length === 0}
              onChange={(e) => {
                const isChecked = !!e?.target?.checked;
                if (isChecked) {
                  if (otherSelectedKeys.length > 0) {
                    message.warning(debtorViewType === 'P' ? '已切换为个人债务人，已自动取消企业债务人勾选' : '已切换为企业债务人，已自动取消个人债务人勾选');
                  }
                  applySelectedDebtors(currentRowKeys, debtorViewType, true);
                  return;
                }
                if (otherSelectedKeys.length > 0) {
                  applySelectedDebtors(otherSelectedKeys, otherType, true);
                  return;
                }
                applySelectedDebtors([], debtorViewType, true);
              }}
            >
              全选
            </Checkbox>
            <span style={{ color: '#666' }}>已选：{selectedDebtorKeys.length} 个</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            {currentDebtorRows.map((row) => {
              const displayName = row?.type === 'E'
                ? (String(row?.companyName || '').trim() || '企业')
                : (String(row?.name || '').trim() || '个人');
              const checked = currentSelectedKeys.includes(row.key);
              return (
                <div key={row.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Checkbox
                    checked={checked}
                    onChange={(e) => {
                      const isChecked = !!e?.target?.checked;
                      if (isChecked) {
                        const nextKeys = Array.from(new Set([...currentSelectedKeys, row.key]));
                        if (otherSelectedKeys.length > 0) {
                          message.warning(debtorViewType === 'P' ? '已切换为个人债务人，已自动取消企业债务人勾选' : '已切换为企业债务人，已自动取消个人债务人勾选');
                        }
                        applySelectedDebtors(nextKeys, debtorViewType, true);
                        return;
                      }

                      const nextKeys = currentSelectedKeys.filter((k) => k !== row.key);
                      if (nextKeys.length > 0) {
                        applySelectedDebtors(nextKeys, debtorViewType, true);
                        return;
                      }
                      if (otherSelectedKeys.length > 0) {
                        applySelectedDebtors(otherSelectedKeys, otherType, true);
                        return;
                      }
                      applySelectedDebtors([], debtorViewType, true);
                    }}
                  />
                  <Button
                    type="link"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => {
                      setDebtorDetailRow(row || null);
                      setDebtorDetailOpen(true);
                    }}
                  >
                    {displayName}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal
        title={debtorDetailRow?.type === 'E' ? '企业债务人详情' : '个人债务人详情'}
        width={640}
        open={debtorDetailOpen}
        onCancel={() => setDebtorDetailOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Descriptions bordered size="small" column={2}>
          {debtorDetailRow?.type === 'E' ? (
            <>
              <Descriptions.Item label="企业名称">{debtorDetailRow?.companyName || '-'}</Descriptions.Item>
              <Descriptions.Item label="统一社会信用代码">{debtorDetailRow?.socialCreditCode || '-'}</Descriptions.Item>
              <Descriptions.Item label="法定代表人姓名">{debtorDetailRow?.legalPersonName || '-'}</Descriptions.Item>
              <Descriptions.Item label="法定代表人身份证">{debtorDetailRow?.legalPersonIdNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{debtorDetailRow?.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{debtorDetailRow?.email || '-'}</Descriptions.Item>
            </>
          ) : (
            <>
              <Descriptions.Item label="姓名">{debtorDetailRow?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="身份证号/证件号">{debtorDetailRow?.idNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{debtorDetailRow?.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{debtorDetailRow?.email || '-'}</Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Modal>
    </>
  );
};

export default DebtInfoAndDebtorSelectorCard;
